import React, { useState } from 'react';
import { App, Button, Card, Col, DatePicker, Descriptions, Form, Input, Row, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, RollbackOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';

const { Text } = Typography;

const AssetIssueDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.assetIssue(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getAssetIssue(id!),
        () => hrmsService.listAssetIssues(),
        id!
      ),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Asset issue" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Asset issue" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const submitReturn = async () => {
    try {
      const v = await form.validateFields();
      setLoading(true);
      await hrmsService.returnAsset(id!, {
        return_date: v.return_date.format('YYYY-MM-DD'),
        condition_on_return: v.condition_on_return,
        notes: v.notes,
      });
      message.success('Returned');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assetIssue(id!) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assetIssues() });
    } catch {
      message.error('Return failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Asset issue"
          documentNumber={row.issue_number  || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/asset-custody')}>
              Back
            </Button>
          }
        />
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Issue details" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Asset code">{row.asset_code || '—'}</Descriptions.Item>
              <Descriptions.Item label="Asset name">{row.asset_name}</Descriptions.Item>
              <Descriptions.Item label="Issued">
                {row.issue_date ? dayjs(row.issue_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Expected return">
                {row.expected_return_date ? dayjs(row.expected_return_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Returned">
                {row.return_date ? dayjs(row.return_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Condition on issue">{row.condition_on_issue || '—'}</Descriptions.Item>
              <Descriptions.Item label="Condition on return">{row.condition_on_return || '—'}</Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>
                {row.notes || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {row.status === 'Issued' && (
            <Card title="Return asset" size="small" style={{ marginBottom: 16 }}>
              <Form form={form} layout="vertical" initialValues={{ return_date: dayjs() }} onFinish={submitReturn}>
                <Form.Item name="return_date" label="Return date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: 220 }} />
                </Form.Item>
                <Form.Item name="condition_on_return" label="Condition on return">
                  <Input />
                </Form.Item>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Button type="primary" htmlType="submit" icon={<RollbackOutlined />} loading={loading}>
                  Record return
                </Button>
              </Form>
            </Card>
          )}

          <EntityAttachmentsCard entityType="HR_ASSET_ISSUE" entityId={row.id} />
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default AssetIssueDetailPage;
